import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Skeleton } from '@/components/ui/skeleton'
import { AuditTimeline } from './AuditTimeline'
import { ListPagination } from './ListPagination'
import { AuditHistoryApi, type AuditObjectType } from '@/services/shared/auditHistoryApi'
import { AuthService } from '@/services/core/auth'

const PAGE_SIZE = 25

export interface AuditHistorySheetProps {
  objectType: AuditObjectType
  objectId: string
  /** Name of the object the history belongs to, shown under the title. */
  objectName?: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * The full change history of one object, in a sheet off the right edge. Paged
 * rather than scrolled endlessly — a long-running object can have hundreds of
 * entries.
 */
export function AuditHistorySheet({
  objectType,
  objectId,
  objectName,
  open,
  onOpenChange,
}: AuditHistorySheetProps) {
  const { t } = useTranslation()
  const [page, setPage] = useState(1)

  const { data, isLoading } = useQuery({
    queryKey: ['audithistory', objectType, objectId, page, PAGE_SIZE],
    queryFn: () => AuditHistoryApi.getPage(objectType, objectId, { page, pageSize: PAGE_SIZE }),
    // Only fetched while the sheet is open: nothing on the page behind it shows
    // this data, so there is nothing to keep warm.
    enabled: open && AuthService.isAuthenticated() && !!objectId,
  })

  const entries = data?.results ?? []
  const totalCount = data?.totalCount ?? 0

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        // Reopening starts at the newest page rather than wherever the last
        // visit left off.
        if (!next) setPage(1)
        onOpenChange(next)
      }}
    >
      <SheetContent className="w-full gap-0 sm:max-w-xl">
        <SheetHeader className="border-b">
          <SheetTitle>{t('audit.title')}</SheetTitle>
          <SheetDescription>{objectName ?? t('audit.description')}</SheetDescription>
        </SheetHeader>

        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          {isLoading && (
            <div className="flex flex-col gap-3">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          )}
          {!isLoading && entries.length === 0 && (
            <p className="text-sm text-(--ad-text-muted)">{t('audit.empty')}</p>
          )}
          {!isLoading && entries.length > 0 && <AuditTimeline entries={entries} />}
        </div>

        {/* ListPagination is a card footer, so it needs the card spacing the
            sheet does not define. Left out entirely for an empty history,
            which would otherwise leave a bordered strip with nothing in it. */}
        {totalCount > 0 && (
          <div className="border-t py-4 [--card-spacing:--spacing(4)]">
            <ListPagination
              page={page}
              pageSize={PAGE_SIZE}
              totalCount={totalCount}
              onPageChange={setPage}
            />
          </div>
        )}
      </SheetContent>
    </Sheet>
  )
}
